import { clientConfig } from '@/config/clientConfig';
import { env } from '@/config/env';
import { primaryNavigation } from '@/shared/config/navigation';
import { useAccessControl } from '@skewb/access-control/react';
import { HelpCircle, PanelLeftClose, PanelLeftOpen, Settings, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export default function Sidebar({
  collapsed: controlledCollapsed,
  onToggleCollapse,
}: SidebarProps = {}) {
  const location = useLocation();
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const collapsed = controlledCollapsed ?? internalCollapsed;
  const toggleCollapse = onToggleCollapse ?? (() => setInternalCollapsed(!internalCollapsed));
  const { ability } = useAccessControl();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Filter navigation items based on permissions
  const visibleNavigation = primaryNavigation.filter(
    (item) =>
      !item.requiredAbility ||
      ability.can(item.requiredAbility.action, item.requiredAbility.subject),
  );

  return (
    <aside
      className={`fixed left-0 top-0 h-screen ${collapsed ? 'w-16' : 'w-64'} bg-white dark:bg-card border-r border-zinc-200 dark:border-border flex flex-col theme-transition transition-all duration-300 print:hidden`}
    >
      {/* Logo */}
      <Link
        to="/"
        className="h-32 flex items-center justify-center px-6 border-b border-zinc-200 dark:border-border theme-transition"
      >
        {collapsed ? (
          <span className="text-lg font-bold" style={{ color: clientConfig.theme.primary }}>
            {clientConfig.name
              .split(' ')
              .map((w) => w[0])
              .join('')}
          </span>
        ) : (
          <img
            src={clientConfig.logo}
            alt={clientConfig.name}
            className="h-auto w-full max-w-[180px] object-contain"
          />
        )}
      </Link>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className={`space-y-1 ${collapsed ? 'px-2' : 'px-3'}`}>
          {visibleNavigation.map((item) => {
            const active = isActive(item.path);
            const Icon = item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  active
                    ? 'text-white'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                } ${collapsed ? 'justify-center' : ''}`}
                style={active ? { backgroundColor: clientConfig.theme.sidebarActiveBg } : undefined}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {!collapsed && <span className="text-sm font-medium">{item.name}</span>}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Bottom Actions */}
      <div
        className={`${collapsed ? 'px-2' : 'px-3'} py-3 border-t border-zinc-200 dark:border-border space-y-2 theme-transition`}
      >
        {env.isDevelopment && (
          <Link
            to="/test-coverage"
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              isActive('/test-coverage')
                ? 'text-white'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
            } ${collapsed ? 'justify-center' : ''}`}
            style={
              isActive('/test-coverage')
                ? { backgroundColor: clientConfig.theme.sidebarActiveBg }
                : undefined
            }
            title={collapsed ? 'Test Coverage' : undefined}
          >
            <ShieldCheck className="w-5 h-5 shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Test Coverage</span>}
          </Link>
        )}
        {/* Settings is visible to `read Settings` OR `read AuditTrail`: the Settings page
            filters its own cards by ability, so an audit-only role sees just the Audit
            Trail card (WRK-5474). */}
        {(ability.can('read', 'Settings') || ability.can('read', 'AuditTrail')) && (
          <Link
            to="/settings"
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              isActive('/settings')
                ? 'text-white'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
            } ${collapsed ? 'justify-center' : ''}`}
            style={
              isActive('/settings')
                ? { backgroundColor: clientConfig.theme.sidebarActiveBg }
                : undefined
            }
          >
            <Settings className="w-5 h-5 shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Settings</span>}
          </Link>
        )}
        {clientConfig.features.helpPage && (
          <Link
            to="/help"
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              isActive('/help')
                ? 'text-white'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
            } ${collapsed ? 'justify-center' : ''}`}
            style={
              isActive('/help')
                ? { backgroundColor: clientConfig.theme.sidebarActiveBg }
                : undefined
            }
          >
            <HelpCircle className="w-5 h-5 shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Help</span>}
          </Link>
        )}
        <button
          onClick={toggleCollapse}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-muted-foreground hover:bg-accent hover:text-foreground ${collapsed ? 'justify-center' : ''}`}
        >
          {collapsed ? (
            <PanelLeftOpen className="w-5 h-5 shrink-0" />
          ) : (
            <>
              <PanelLeftClose className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
